/**
 * BadgeUnlockModal — celebrates a freshly earned achievement.
 *
 * Usage:
 *   const [unlocked, setUnlocked] = useState([]);
 *   ...
 *   const r = await axios.post('/api/catches', payload);
 *   if (r.data.badges_unlocked?.length) setUnlocked(r.data.badges_unlocked);
 *   ...
 *   <BadgeUnlockModal badges={unlocked} onClose={() => setUnlocked([])} />
 *
 * If multiple badges arrive at once we cycle through them on click. The first
 * unlock is the headline so we show that immediately.
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Award, Crown, MapPin, Snowflake, Star, Gem, Camera, Shield, X, ChevronRight, Sparkles } from 'lucide-react';

const RARITY_STYLES = {
  common:    { ring: 'ring-2 ring-[#8a948a]/60',   bg: 'from-[#1a1d1a] to-[#0f110f]', accent: 'text-[#c0c4c0]', label: 'Common' },
  rare:      { ring: 'ring-2 ring-[#c8b273]/70',   bg: 'from-[#1a1d1a] via-[#1a1d1a] to-[#3a2f12]', accent: 'text-[#c8b273]', label: 'Rare' },
  epic:      { ring: 'ring-2 ring-[#ff5e00]/70',   bg: 'from-[#1a1410] via-[#1a1410] to-[#3a1b0a]', accent: 'text-[#ff5e00]', label: 'Epic' },
  legendary: { ring: 'ring-2 ring-[#c8b273]',      bg: 'from-[#1a1410] via-[#3a2f12] to-[#7d6730]', accent: 'text-[#c8b273]', label: 'Legendary' },
};

const ICONS = {
  trophy: Trophy,
  award: Award,
  crown: Crown,
  'map-pin': MapPin,
  snowflake: Snowflake,
  star: Star,
  gem: Gem,
  camera: Camera,
  shield: Shield,
};

const BadgeUnlockModal = ({ badges = [], onClose }) => {
  const [idx, setIdx] = useState(0);

  // Reset to first badge whenever a new batch arrives
  useEffect(() => { setIdx(0); }, [badges]);

  if (!badges || badges.length === 0) return null;

  const b = badges[idx] || badges[0];
  const style = RARITY_STYLES[b.rarity] || RARITY_STYLES.common;
  const Icon = ICONS[b.icon] || Trophy;
  const isLast = idx >= badges.length - 1;

  const handleNext = () => {
    if (isLast) onClose?.();
    else setIdx(i => i + 1);
  };

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="badge-unlock-modal"
      >
        <motion.div
          key={b.code}
          initial={{ scale: 0.85, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 22 }}
          className={`relative max-w-md w-full rounded-2xl border border-white/10 bg-gradient-to-br ${style.bg} p-8 text-center shadow-[0_30px_80px_rgba(0,0,0,0.65)]`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 text-[#8a948a] hover:text-white transition-colors"
            data-testid="badge-modal-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Sparkles confetti */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
            {[...Array(12)].map((_, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 0, x: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  y: [0, -60 - Math.random() * 80],
                  x: [(i - 6) * 12, (i - 6) * 22],
                  scale: [0, 1, 0.3],
                }}
                transition={{ duration: 1.6, delay: 0.1 + i * 0.04, repeat: Infinity, repeatDelay: 1.4 }}
                className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full bg-[#c8b273] shadow-[0_0_10px_rgba(200,178,115,0.8)]"
              />
            ))}
          </div>

          <p className="data-label text-[#c8b273] mb-1 relative">
            <Sparkles className="w-3 h-3 inline-block mr-1 -mt-1" />
            Achievement Unlocked
          </p>

          {/* Badge icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.15 }}
            className={`mx-auto my-4 w-24 h-24 rounded-full bg-[#0b0d0b] ${style.ring} flex items-center justify-center relative`}
          >
            <Icon className={`w-12 h-12 ${style.accent}`} />
            <div className={`absolute -inset-1 rounded-full ${style.ring.replace('ring-2', 'ring-4').replace('/60', '/15').replace('/70', '/20')} blur-md`} />
          </motion.div>

          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-wider text-white mb-2">
            {b.name}
          </h2>
          <p className={`data-label ${style.accent} mb-3 uppercase`}>
            {style.label}
          </p>
          <p className="text-sm text-[#c0c4c0] mb-6 leading-relaxed">
            {b.description || 'A new badge has been added to your collection.'}
          </p>

          <button
            type="button"
            onClick={handleNext}
            data-testid="badge-modal-next"
            className="w-full inline-flex items-center justify-center gap-2 rounded bg-[#c8b273] px-5 py-3 text-sm font-bold uppercase tracking-wider text-black transition-colors hover:bg-[#d8c283]"
          >
            {isLast ? 'Awesome' : (
              <>
                Next ({idx + 1}/{badges.length})
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BadgeUnlockModal;
